/**
 * GeneralSettingsForm - Allgemeine Studio-Einstellungen
 * Extrahiert aus SettingsPage.js für bessere Wartbarkeit
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Save, Building } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { PhoneInput } from '@/components/ui/phone-input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GeneralSettingsForm = () => {
  const [settings, setSettings] = useState({
    studio_name: '',
    app_url: '',
    studio_address: '',
    studio_phone: '',
    studio_email: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // AUTOMATISCH: Aktuelle Browser-URL als app_url verwenden
      const currentUrl = window.location.origin;
      
      setSettings({
        studio_name: response.data.studio_name || '',
        app_url: currentUrl, // Immer die aktuelle URL verwenden
        studio_address: response.data.studio_address || '',
        studio_phone: response.data.studio_phone || '',
        studio_email: response.data.studio_email || ''
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Einstellungen gespeichert!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Laden...</div>;
  }

  return (
    <Card data-testid="general-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building size={20} className="text-blue-600" />
          Allgemeine Einstellungen
        </CardTitle>
        <CardDescription>
          Grundlegende Studio-Informationen
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Studio-Name */}
        <div className="space-y-2">
          <Label htmlFor="studio_name">Studio-Name</Label>
          <Input
            id="studio_name"
            data-testid="studio-name-input"
            value={settings.studio_name}
            onChange={(e) => setSettings({...settings, studio_name: e.target.value})}
            placeholder="z.B. StrongFit"
          />
          <p className="text-xs text-gray-500">Wird in E-Mails und Nachrichten verwendet</p>
        </div>

        {/* App-URL - AUTOMATISCH */}
        <div className="space-y-2">
          <Label htmlFor="app_url">App-URL (für Verifizierungs-Links)</Label>
          <div className="flex gap-2">
            <Input
              id="app_url"
              data-testid="app-url-input"
              value={settings.app_url}
              readOnly
              className="bg-green-50 border-green-300"
            />
            <span className="text-green-600 text-sm flex items-center whitespace-nowrap">
              ✓ Automatisch
            </span>
          </div>
          <p className="text-xs text-green-600">
            Diese URL wird automatisch aus Ihrer aktuellen Browser-Adresse ermittelt.
          </p>
        </div>

        {/* Studio-Adresse */}
        <div className="space-y-2">
          <Label htmlFor="studio_address">Adresse</Label>
          <Input
            id="studio_address"
            data-testid="studio-address-input"
            value={settings.studio_address}
            onChange={(e) => setSettings({...settings, studio_address: e.target.value})}
            placeholder="Musterstrasse 1, 8000 Zürich"
          />
        </div>

        {/* Studio-Telefon */}
        <div className="space-y-2">
          <Label htmlFor="studio_phone">Telefon</Label>
          <PhoneInput
            id="studio_phone"
            data-testid="studio-phone-input"
            value={settings.studio_phone}
            onChange={(e) => setSettings({...settings, studio_phone: e.target.value})}
          />
        </div>

        {/* Studio-Email */}
        <div className="space-y-2">
          <Label htmlFor="studio_email">Kontakt-E-Mail</Label>
          <Input
            id="studio_email"
            data-testid="studio-email-input"
            type="email"
            value={settings.studio_email}
            onChange={(e) => setSettings({...settings, studio_email: e.target.value})}
            placeholder="info@strongfit.ch"
          />
        </div>

        <Button 
          onClick={saveSettings} 
          disabled={saving} 
          className="w-full"
          data-testid="save-general-settings-btn"
        >
          <Save size={16} className="mr-2" />
          {saving ? 'Speichern...' : 'Einstellungen speichern'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default GeneralSettingsForm;
